import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, InputGroup, Alert, Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  Mail, RefreshCw, Star, Archive, Trash2, Search,
  Paperclip, Reply, Send, Forward
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useNotifications } from '../context/NotificationContext';
import EmailComposer from './EmailComposer';
import './IncomingEmails.css';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const containerVariants = {
  animate: { transition: { staggerChildren: 0.03 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25 } }
};

function IncomingEmails() {
  const { refresh: refreshNotifications } = useNotifications();
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    is_read: '',
    is_archived: '',
    search: ''
  });
  const [pagination, setPagination] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [forwardMessage, setForwardMessage] = useState('');
  const [forwardTo, setForwardTo] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [forwardAttachments, setForwardAttachments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  const [sendingForward, setSendingForward] = useState(false);
  const autoFetchIntervalRef = useRef(null);

  useEffect(() => {
    fetchEmails();
    fetchStats();
    startAutoFetch();

    return () => {
      if (autoFetchIntervalRef.current) {
        clearInterval(autoFetchIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const startAutoFetch = () => {
    if (autoFetchIntervalRef.current) {
      clearInterval(autoFetchIntervalRef.current);
    }
    autoFetchIntervalRef.current = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        await fetchNewEmails(true);
      } catch (err) {
        console.error('Auto-fetch error:', err);
      }
    }, 120000);
  };

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      const response = await api.get(`/admin/emails?${params.toString()}`);
      setEmails(response.data.emails);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to load emails');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/emails/stats');
      setStats(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchNewEmails = async (silent = false) => {
    if (!silent) setFetching(true);
    try {
      const response = await api.post('/admin/emails/fetch', { unreadOnly: true });
      if (!silent && response.data.count > 0) {
        toast.success(`Fetched ${response.data.count} new emails`);
      }
      fetchEmails();
      fetchStats();
      setError('');
    } catch (err) {
      if (!silent) {
        const errorMsg = err.response?.data?.message || err.message;
        toast.error(errorMsg);
        setError(errorMsg);
      }
    } finally {
      if (!silent) setFetching(false);
    }
  };

  const viewEmail = async (id) => {
    try {
      const response = await api.get(`/admin/emails/${id}`);
      setSelectedEmail(response.data);
      setShowEmailModal(true);
      fetchEmails();
      fetchStats();
      refreshNotifications();
    } catch (err) {
      toast.error('Failed to load email');
    }
  };

  const handleReply = () => {
    if (selectedEmail) {
      setReplyMessage('');
      setReplyAttachments([]);
      setShowReplyModal(true);
    }
  };

  const handleForward = () => {
    if (selectedEmail) {
      setForwardMessage('');
      setForwardTo('');
      setForwardAttachments([]);
      setShowForwardModal(true);
    }
  };

  const closeReplyModal = () => {
    setShowReplyModal(false);
    setTimeout(() => {
      setReplyMessage('');
      setReplyAttachments([]);
    }, 300);
  };

  const closeForwardModal = () => {
    setShowForwardModal(false);
    setTimeout(() => {
      setForwardMessage('');
      setForwardTo('');
      setForwardAttachments([]);
    }, 300);
  };

  const sendReply = async () => {
    const textContent = replyMessage.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      toast.error('Please enter a message');
      return;
    }
    setSendingReply(true);
    try {
      const formData = new FormData();
      formData.append('message', replyMessage);
      replyAttachments.forEach((att) => {
        formData.append('attachments', att.file);
      });
      await api.post(`/admin/emails/${selectedEmail.email.id}/reply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Reply sent successfully');
      closeReplyModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const sendForward = async () => {
    if (!forwardTo.trim()) {
      toast.error('Please enter recipient email');
      return;
    }
    const textContent = forwardMessage.replace(/<[^>]*>/g, '').trim();
    if (!textContent && forwardAttachments.length === 0) {
      toast.error('Please enter a message or add attachments');
      return;
    }
    setSendingForward(true);
    try {
      const formData = new FormData();
      formData.append('to', forwardTo);
      if (forwardMessage.trim()) {
        formData.append('message', forwardMessage);
      }
      forwardAttachments.forEach((att) => {
        formData.append('attachments', att.file);
      });
      await api.post(`/admin/emails/${selectedEmail.email.id}/forward`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Email forwarded successfully');
      closeForwardModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to forward email');
    } finally {
      setSendingForward(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const toggleRead = async (id, currentStatus) => {
    try {
      await api.put(`/admin/emails/${id}/read`, { is_read: !currentStatus });
      fetchEmails();
      fetchStats();
      refreshNotifications();
      toast.success('Email updated');
    } catch (err) {
      toast.error('Failed to update email');
    }
  };

  const toggleArchive = async (id, currentStatus) => {
    try {
      await api.put(`/admin/emails/${id}/archive`, { is_archived: !currentStatus });
      fetchEmails();
      fetchStats();
      toast.success('Email archived');
    } catch (err) {
      toast.error('Failed to update email');
    }
  };

  const toggleStar = async (id, currentStatus) => {
    try {
      await api.put(`/admin/emails/${id}/star`, { is_starred: !currentStatus });
      fetchEmails();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update email');
    }
  };

  const deleteEmail = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email?')) return;
    try {
      await api.delete(`/admin/emails/${id}`);
      toast.success('Email deleted');
      fetchEmails();
      fetchStats();
      if (selectedEmail?.email?.id === id) {
        setShowEmailModal(false);
        setSelectedEmail(null);
      }
    } catch (err) {
      toast.error('Failed to delete email');
    }
  };

  const toggleSelectEmail = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === emails.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(emails.map(e => e.id)));
    }
  };

  const bulkDeleteEmails = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected email(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await api.post('/admin/emails/bulk-delete', { ids: Array.from(selectedIds) });
      toast.success(`${selectedIds.size} email(s) deleted`);
      setSelectedIds(new Set());
      fetchEmails();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete emails');
    } finally {
      setBulkDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      className="incoming-emails-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Header */}
      <div className="emails-header">
        <div className="header-left">
          <h1 className="emails-title">Inbox</h1>
          {stats && (
            <span className="unread-badge">{stats.unread || 0} unread</span>
          )}
        </div>
        <div className="header-actions">
          {selectedIds.size > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={bulkDeleteEmails}
              disabled={bulkDeleting}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: 'var(--radius-full)', padding: '8px 18px' }}
            >
              <Trash2 size={14} />
              {bulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size} selected`}
            </Button>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => fetchNewEmails()}
            disabled={fetching}
            className="refresh-btn"
          >
            <RefreshCw size={14} className={fetching ? 'spinning' : ''} />
            {fetching ? 'Fetching...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{stats.total || 0}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item">
            <span className="stat-value unread">{stats.unread || 0}</span>
            <span className="stat-label">Unread</span>
          </div>
          <div className="stat-item">
            <span className="stat-value starred">{stats.starred || 0}</span>
            <span className="stat-label">Starred</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.last_24h || 0}</span>
            <span className="stat-label">Last 24h</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <InputGroup className="search-box">
          <InputGroup.Text><Search size={14} /></InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search emails..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          />
        </InputGroup>
        <Form.Select
          className="filter-select"
          value={filters.is_read}
          onChange={(e) => setFilters({ ...filters, is_read: e.target.value, page: 1 })}
        >
          <option value="">All</option>
          <option value="false">Unread</option>
          <option value="true">Read</option>
        </Form.Select>
        <Form.Select
          className="filter-select"
          value={filters.is_archived}
          onChange={(e) => setFilters({ ...filters, is_archived: e.target.value, page: 1 })}
        >
          <option value="">All</option>
          <option value="false">Active</option>
          <option value="true">Archived</option>
        </Form.Select>
      </div>

      {error && <Alert variant="danger" className="error-alert">{error}</Alert>}

      {/* Email List */}
      <div className="emails-list-container">
        {loading ? (
          <div className="loading-center">
            <div className="loading-spinner" />
          </div>
        ) : emails.length === 0 ? (
          <div className="empty-state">
            <Mail size={56} strokeWidth={1.2} />
            <p>No emails found</p>
            <Button variant="primary" onClick={() => fetchNewEmails()}>
              Fetch Emails
            </Button>
          </div>
        ) : (
          <>
            {/* Select All bar */}
            {emails.length > 0 && (
              <div className="select-all-bar">
                <label className="select-all-label" onClick={(e) => { e.preventDefault(); toggleSelectAll(); }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.size === emails.length && emails.length > 0}
                    onChange={toggleSelectAll}
                  />
                  <span>{selectedIds.size > 0 ? `${selectedIds.size} of ${emails.length} selected` : 'Select all'}</span>
                </label>
              </div>
            )}
            <motion.div
              className="emails-list"
              variants={containerVariants}
              initial="initial"
              animate="animate"
            >
              {emails.map((email) => (
                <motion.div
                  key={email.id}
                  className={`email-item ${!email.is_read ? 'unread' : ''} ${selectedIds.has(email.id) ? 'selected' : ''}`}
                  onClick={() => viewEmail(email.id)}
                  variants={itemVariants}
                >
                  <div className="email-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(email.id)}
                      onChange={(e) => toggleSelectEmail(email.id, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="email-star" onClick={(e) => {
                    e.stopPropagation();
                    toggleStar(email.id, email.is_starred);
                  }}>
                    <Star
                      size={16}
                      className={email.is_starred ? 'starred' : ''}
                      fill={email.is_starred ? 'currentColor' : 'none'}
                    />
                  </div>
                  <div className="email-content">
                    <div className="email-header">
                      <span className="email-from">{email.from_email}</span>
                      <span className="email-date">{formatDate(email.received_at)}</span>
                    </div>
                    <div className="email-subject">{email.subject || '(No Subject)'}</div>
                    <div className="email-preview">{email.text_preview || ''}</div>
                    {email.attachment_count > 0 && (
                      <div className="email-attachments">
                        <Paperclip size={12} /> {email.attachment_count} attachment{email.attachment_count > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  <div className="email-actions" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => toggleArchive(email.id, email.is_archived)}
                      title={email.is_archived ? 'Unarchive' : 'Archive'}
                    >
                      <Archive size={15} />
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => deleteEmail(email.id)}
                      className="text-danger"
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {pagination && pagination.totalPages > 1 && (
              <div className="pagination-bar">
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                >
                  Previous
                </Button>
                <span className="page-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Detail Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg" className="email-modal">
        <Modal.Header closeButton className="email-modal-header">
          <Modal.Title className="email-modal-title">
            {selectedEmail?.email?.subject || '(No Subject)'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="email-modal-body">
          {selectedEmail && (
            <>
              <div className="email-detail-header">
                <div className="email-detail-from">
                  <strong>{selectedEmail.email.from_email}</strong>
                  <span className="email-detail-date">
                    {new Date(selectedEmail.email.received_at).toLocaleString()}
                  </span>
                </div>
                <div className="email-detail-to">
                  <span className="label">To:</span> {selectedEmail.email.to_email}
                </div>
              </div>

              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div className="email-attachments-list">
                  <strong>Attachments:</strong>
                  <ul>
                    {selectedEmail.attachments.map((att) => (
                      <li key={att.id}>
                        <Paperclip size={13} /> {att.filename} ({(att.size_bytes / 1024).toFixed(1)} KB)
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                className="email-body-content"
                dangerouslySetInnerHTML={{ __html: selectedEmail.email.html_content || selectedEmail.email.text_content }}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="email-modal-footer">
          <Button variant="primary" onClick={handleReply}>
            <Reply size={15} /> Reply
          </Button>
          <Button variant="outline-primary" onClick={handleForward}>
            <Forward size={15} /> Forward
          </Button>
          <Button
            variant="outline-warning"
            onClick={() => {
              toggleStar(selectedEmail?.email?.id, selectedEmail?.email?.is_starred);
              setSelectedEmail({
                ...selectedEmail,
                email: { ...selectedEmail.email, is_starred: !selectedEmail.email.is_starred }
              });
            }}
          >
            <Star
              size={15}
              className={selectedEmail?.email?.is_starred ? 'text-warning' : ''}
              fill={selectedEmail?.email?.is_starred ? 'currentColor' : 'none'}
            />
            {selectedEmail?.email?.is_starred ? 'Unstar' : 'Star'}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => {
              toggleArchive(selectedEmail?.email?.id, selectedEmail?.email?.is_archived);
              setShowEmailModal(false);
            }}
          >
            <Archive size={15} /> {selectedEmail?.email?.is_archived ? 'Unarchive' : 'Archive'}
          </Button>
          <Button
            variant="outline-danger"
            onClick={() => {
              deleteEmail(selectedEmail?.email?.id);
              setShowEmailModal(false);
            }}
          >
            <Trash2 size={15} /> Delete
          </Button>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reply Modal */}
      <Modal show={showReplyModal} onHide={closeReplyModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Reply to {selectedEmail?.email?.from_email}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <EmailComposer
              key={`reply-${showReplyModal}`}
              message={replyMessage}
              setMessage={setReplyMessage}
              attachments={replyAttachments}
              setAttachments={setReplyAttachments}
              placeholder="Type your reply here..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeReplyModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={sendReply}
            disabled={sendingReply || !replyMessage.replace(/<[^>]*>/g, '').trim()}
          >
            <Send size={15} /> {sendingReply ? 'Sending...' : 'Send Reply'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Forward Modal */}
      <Modal show={showForwardModal} onHide={closeForwardModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Forward Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>To</Form.Label>
            <Form.Control
              type="email"
              value={forwardTo}
              onChange={(e) => setForwardTo(e.target.value)}
              placeholder="recipient@example.com"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Message (Optional)</Form.Label>
            <EmailComposer
              key={`forward-${showForwardModal}`}
              message={forwardMessage}
              setMessage={setForwardMessage}
              attachments={forwardAttachments}
              setAttachments={setForwardAttachments}
              placeholder="Add a note (optional)..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeForwardModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={sendForward}
            disabled={sendingForward || !forwardTo.trim()}
          >
            <Send size={15} /> {sendingForward ? 'Sending...' : 'Forward'}
          </Button>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
}

export default IncomingEmails;
